// context/MessagesContext.jsx
import React, {
  createContext,
  useReducer,
  useCallback,
  useRef,
  useEffect,
  useState,
} from "react";
import api from "../services/api";
import encryption from "../services/encryption";
import socket from "../services/socket";
import { useAuth } from "./AuthContext";
import { useContacts } from "./ContactsContext";
import { useNotification } from "../hooks/useNotification";

const MessagesContext = createContext();

const initialState = {
  messages: [],
  typingUsers: {},
  sessions: {},
  isLoading: false,
  error: null,
  currentMessage: "",
  pendingSelfDestructMessage: "",
  shouldAutoScroll: true,
  unreadCounts: {},
  pendingFile: null,
};

const ACTIONS = {
  SET_MESSAGES: "SET_MESSAGES",
  ADD_MESSAGE: "ADD_MESSAGE",
  UPDATE_MESSAGE: "UPDATE_MESSAGE",
  DELETE_MESSAGE: "DELETE_MESSAGE",
  SET_TYPING: "SET_TYPING",
  SET_SESSION: "SET_SESSION",
  SET_LOADING: "SET_LOADING",
  SET_ERROR: "SET_ERROR",
  CLEAR_ERROR: "CLEAR_ERROR",
  SET_CURRENT_MESSAGE: "SET_CURRENT_MESSAGE",
  SET_PENDING_SELF_DESTRUCT: "SET_PENDING_SELF_DESTRUCT",
  SET_SHOULD_AUTO_SCROLL: "SET_SHOULD_AUTO_SCROLL",
  CLEAR_MESSAGES: "CLEAR_MESSAGES",
  INCREMENT_UNREAD: "INCREMENT_UNREAD",
  RESET_UNREAD: "RESET_UNREAD",
  SET_PENDING_FILE: "SET_PENDING_FILE",
  CLEAR_PENDING_FILE: "CLEAR_PENDING_FILE",
};

function messagesReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_MESSAGES:
      return { ...state, messages: action.payload };

    case ACTIONS.ADD_MESSAGE:
      const existingIndex = state.messages.findIndex((m) => m.id === action.payload.id);
      if (existingIndex !== -1) {
        const updatedMessages = [...state.messages];
        updatedMessages[existingIndex] = { ...updatedMessages[existingIndex], ...action.payload };
        return { ...state, messages: updatedMessages };
      }
      return { ...state, messages: [...state.messages, action.payload] };

    case ACTIONS.UPDATE_MESSAGE:
      return {
        ...state,
        messages: state.messages.map((msg) =>
          msg.id === action.payload.id
            ? { ...msg, ...action.payload.updates }
            : msg,
        ),
      };

    case ACTIONS.DELETE_MESSAGE:
      return {
        ...state,
        messages: state.messages.filter((msg) => msg.id !== action.payload),
      };

    case ACTIONS.SET_TYPING:
      return {
        ...state,
        typingUsers: { ...state.typingUsers, ...action.payload },
      };

    case ACTIONS.SET_SESSION:
      return {
        ...state,
        sessions: { ...state.sessions, ...action.payload },
      };

    case ACTIONS.SET_LOADING:
      return { ...state, isLoading: action.payload };

    case ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, isLoading: false };

    case ACTIONS.CLEAR_ERROR:
      return { ...state, error: null };

    case ACTIONS.SET_CURRENT_MESSAGE:
      return { ...state, currentMessage: action.payload };

    case ACTIONS.SET_PENDING_SELF_DESTRUCT:
      return { ...state, pendingSelfDestructMessage: action.payload };

    case ACTIONS.SET_SHOULD_AUTO_SCROLL:
      return { ...state, shouldAutoScroll: action.payload };

    case ACTIONS.CLEAR_MESSAGES:
      return { ...state, messages: [] };

    case ACTIONS.INCREMENT_UNREAD:
      return {
        ...state,
        unreadCounts: {
          ...state.unreadCounts,
          [action.payload]: (state.unreadCounts[action.payload] || 0) + 1,
        },
      };

    case ACTIONS.RESET_UNREAD:
      return {
        ...state,
        unreadCounts: {
          ...state.unreadCounts,
          [action.payload]: 0,
        },
      };

    case ACTIONS.SET_PENDING_FILE:
      return { ...state, pendingFile: action.payload };

    case ACTIONS.CLEAR_PENDING_FILE:
      return { ...state, pendingFile: null };

    default:
      return state;
  }
}

export function MessagesProvider({ children }) {
  const [state, dispatch] = useReducer(messagesReducer, initialState);
  const { user, jwtToken, isAuthenticated } = useAuth();
  const { selectedContact, updateContactLastMessage, contacts } = useContacts();
  const { showNotification } = useNotification();
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const socketListenersSetup = useRef(false);
  const decryptionInProgress = useRef(false);
  const typingTimeoutRef = useRef(null);
  const [isSelfDestructModalOpen, setIsSelfDestructModalOpen] = useState(false);

  const scrollToBottom = useCallback(
    (behavior = "smooth") => {
      if (messagesEndRef.current && state.shouldAutoScroll) {
        messagesEndRef.current.scrollIntoView({ behavior });
      }
    },
    [state.shouldAutoScroll],
  );

  const handleScroll = useCallback(() => {
    if (!messagesContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } =
      messagesContainerRef.current;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
    dispatch({ type: ACTIONS.SET_SHOULD_AUTO_SCROLL, payload: isNearBottom });
  }, []);

  const loadMessages = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const data = await api.getMessages(100);
      const privateKey = encryption.getPrivateKey();
      
      const formattedMessages = data.messages.map((msg) => {
        const isOwn = msg.senderUuid === user.uuid;
        let decrypted = null;
        
        if (privateKey) {
          try {
            decrypted = encryption.decryptMessage(
              {
                encryptedData: msg.encryptedData,
                iv: msg.iv,
                encryptedKey: msg.encryptedKey,
                senderEncryptedKey: msg.senderEncryptedKey,
                recipientEncryptedKey: msg.recipientEncryptedKey,
              },
              privateKey,
              isOwn
            );
          } catch (error) {
            console.error('Auto-decryption failed for message:', msg.id, error);
          }
        }
        
        return {
          id: msg.id,
          senderUuid: msg.senderUuid,
          recipientUuid: msg.recipientUuid,
          senderToken: msg.senderToken,
          recipientToken: msg.recipientToken,
          encryptedKey: msg.encryptedKey,
          senderEncryptedKey: msg.senderEncryptedKey,
          recipientEncryptedKey: msg.recipientEncryptedKey,
          encryptedData: msg.encryptedData,
          iv: msg.iv,
          sessionId: msg.sessionId,
          timestamp: new Date(msg.timestamp),
          deliveredAt: msg.deliveredAt ? new Date(msg.deliveredAt) : null,
          readAt: msg.readAt ? new Date(msg.readAt) : null,
          expiresAt: msg.expiresAt ? new Date(msg.expiresAt) : null,
          isSelfDestruct: msg.isSelfDestruct,
          decrypted: decrypted,
          isOwn: isOwn,
          hasAttachment: msg.hasAttachment || false,
          attachment: msg.attachment || null,
        };
      });

      dispatch({ type: ACTIONS.SET_MESSAGES, payload: formattedMessages });
    } catch (error) {
      console.error("Load messages error:", error);
    }
  }, [isAuthenticated, user.uuid]);

  const addMessage = useCallback((message) => {
    dispatch({ type: ACTIONS.ADD_MESSAGE, payload: message });
  }, []);

  const updateMessage = useCallback((messageId, updates) => {
    dispatch({
      type: ACTIONS.UPDATE_MESSAGE,
      payload: { id: messageId, updates },
    });
  }, []);

const sendMessage = useCallback(
  async (messageText, isSelfDestruct = false, expirySeconds = null, attachedFile = null) => {
    console.log("🔵 [sendMessage] START - Message:", messageText);
    console.log("🔵 [sendMessage] isSelfDestruct:", isSelfDestruct);
    console.log("🔵 [sendMessage] expirySeconds:", expirySeconds);
    console.log("🔵 [sendMessage] attachedFile:", !!attachedFile);
    
    const hasContent = messageText && messageText.trim();
    const hasFile = !!attachedFile;
    
    // Allow sending if there's content OR a file
    if (!hasContent && !hasFile) {
      console.log("🔴 [sendMessage] Empty message and no file");
      showNotification("Message cannot be empty.", "error");
      return;
    }

    if (!selectedContact) {
      console.log("🔴 [sendMessage] No selected contact");
      showNotification("No recipient selected.", "error");
      return;
    }

    if (!selectedContact.publicKey) {
      console.log("🔴 [sendMessage] No recipient public key");
      showNotification("Recipient public key not available.", "error");
      return;
    }

    if (!socket.isConnected()) {
      console.log("🔴 [sendMessage] Socket not connected");
      showNotification("Not connected to chat server.", "warning");
      return;
    }

    try {
      const myPublicKey = user.publicKey;
      
      if (!myPublicKey) {
        console.log("🔴 [sendMessage] No sender public key");
        showNotification("Your public key not found. Please re-login.", "error");
        return;
      }

      // Build the message content
      let messageContent = messageText || "";
      let fileMetadata = null;
      
      if (attachedFile) {
        fileMetadata = {
          fileId: attachedFile.fileId,
          fileName: attachedFile.fileName,
          fileSize: attachedFile.fileSize,
          mimeType: attachedFile.mimeType
        };
        
        // If there's no message text, add a default message
        if (!messageContent.trim()) {
          messageContent = `📎 Sent a file: ${attachedFile.fileName}`;
        }
        // IMPORTANT: If there IS message text, keep it as is and include file info separately
      }

      console.log("🟢 [sendMessage] Final message content:", messageContent);
      console.log("🟢 [sendMessage] File metadata:", fileMetadata);

      // Encrypt the message content (with or without file metadata embedded)
      let messagePackage;
      try {
        messagePackage = encryption.prepareDoubleEncryptionMessage(
          messageContent,
          selectedContact.publicKey,
          myPublicKey
        );
        console.log("🟢 [sendMessage] Message package prepared successfully");
      } catch (encryptError) {
        console.error("🔴 [sendMessage] Encryption failed:", encryptError);
        showNotification("Encryption failed: " + encryptError.message, "error");
        return;
      }

      const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      console.log("🟢 [sendMessage] Generated tempId:", tempId);

      const newMessage = {
        id: tempId,
        senderUuid: user.uuid,
        recipientUuid: selectedContact.contactUuid,
        senderToken: user.sessionToken,
        recipientToken: selectedContact.contactToken,
        senderEncryptedKey: messagePackage.senderEncryptedKey,
        recipientEncryptedKey: messagePackage.recipientEncryptedKey,
        encryptedData: messagePackage.encryptedData,
        iv: messagePackage.iv,
        timestamp: new Date(),
        deliveredAt: null,
        readAt: null,
        expiresAt: expirySeconds
          ? new Date(Date.now() + expirySeconds * 1000)
          : null,
        isSelfDestruct,
        decrypted: messageContent,
        isOwn: true,
        hasAttachment: !!attachedFile,
        attachment: fileMetadata,
      };

      console.log("🟢 [sendMessage] Adding message to local state");
      addMessage(newMessage);
      
      console.log("🟢 [sendMessage] Scrolling to bottom");
      setTimeout(() => scrollToBottom("smooth"), 100);

      const socketData = {
        tempId,
        recipientUuid: selectedContact.contactUuid,
        senderEncryptedKey: messagePackage.senderEncryptedKey,
        recipientEncryptedKey: messagePackage.recipientEncryptedKey,
        encryptedData: messagePackage.encryptedData,
        iv: messagePackage.iv,
        expiresIn: expirySeconds,
        isSelfDestruct,
        fileId: attachedFile?.fileId || null,
      };
      
      console.log("🟢 [sendMessage] Emitting sendMessage event");
      console.log("🟢 [sendMessage] Socket data:", {
        hasFileId: !!socketData.fileId,
        fileId: socketData.fileId,
        messageText: messageText
      });
      
      const eventName = attachedFile ? 'sendMessageWithFile' : 'sendMessage';
      
      socket.emit(eventName, socketData, (acknowledgement) => {
        console.log("🟢 [sendMessage] Socket acknowledgement received:", acknowledgement);
        if (acknowledgement && acknowledgement.error) {
          console.error("🔴 [sendMessage] Server error:", acknowledgement.error);
          showNotification("Server error: " + acknowledgement.error, "error");
        } else {
          console.log("🟢 [sendMessage] Message successfully sent to server");
        }
      });

      dispatch({ type: ACTIONS.SET_CURRENT_MESSAGE, payload: "" });
      dispatch({ type: ACTIONS.CLEAR_PENDING_FILE });
      
      showNotification(attachedFile ? "Message with file sent!" : "Message sent!", "success");

      updateContactLastMessage(selectedContact.id, newMessage.timestamp);
      
    } catch (error) {
      console.error("🔴 [sendMessage] ERROR:", error);
      showNotification("Failed to send message: " + error.message, "error");
    }
  },
  [
    selectedContact,
    user.uuid,
    user.sessionToken,
    user.publicKey,
    scrollToBottom,
    showNotification,
    updateContactLastMessage,
    addMessage,
  ],
);

  const setCurrentMessage = useCallback((message) => {
    dispatch({ type: ACTIONS.SET_CURRENT_MESSAGE, payload: message });
  }, []);

  const setPendingSelfDestructMessage = useCallback((message) => {
    dispatch({ type: ACTIONS.SET_PENDING_SELF_DESTRUCT, payload: message });
  }, []);

  const setPendingFile = useCallback((file) => {
    dispatch({ type: ACTIONS.SET_PENDING_FILE, payload: file });
  }, []);

  const clearPendingFile = useCallback(() => {
    dispatch({ type: ACTIONS.CLEAR_PENDING_FILE });
  }, []);

  const deleteMessage = useCallback(
    async (messageId) => {
      try {
        await api.deleteMessage(messageId);
        dispatch({ type: ACTIONS.DELETE_MESSAGE, payload: messageId });
        showNotification("Message deleted", "info");
      } catch (error) {
        console.error("Delete message error:", error);
      }
    },
    [showNotification],
  );

  const markAsDelivered = useCallback(
    async (messageId) => {
      try {
        await api.markMessageDelivered(messageId);
        updateMessage(messageId, { deliveredAt: new Date() });
      } catch (error) {
        console.error("Mark as delivered error:", error);
      }
    },
    [updateMessage],
  );

  const markAsRead = useCallback(
    async (messageId) => {
      try {
        await api.markMessageRead(messageId);
        updateMessage(messageId, { readAt: new Date() });

        const message = state.messages.find((m) => m.id === messageId);
        if (message && message.senderUuid) {
          dispatch({ type: ACTIONS.RESET_UNREAD, payload: message.senderUuid });
        }
      } catch (error) {
        console.error("Mark as read error:", error);
      }
    },
    [updateMessage, state.messages],
  );

  const setTyping = useCallback((contactUuid, isTyping) => {
    dispatch({
      type: ACTIONS.SET_TYPING,
      payload: { [contactUuid]: isTyping },
    });
  }, []);

  const incrementUnread = useCallback((senderUuid) => {
    dispatch({ type: ACTIONS.INCREMENT_UNREAD, payload: senderUuid });
  }, []);

  const resetUnread = useCallback((contactUuid) => {
    dispatch({ type: ACTIONS.RESET_UNREAD, payload: contactUuid });
  }, []);

  const clearMessages = useCallback(() => {
    dispatch({ type: ACTIONS.CLEAR_MESSAGES });
  }, []);

  const decryptAllMessages = useCallback((privateKey) => {
    if (!privateKey) {
      showNotification("No private key provided", "error");
      return false;
    }

    try {
      encryption.setPrivateKey(privateKey);
      
      let decryptedCount = 0;
      
      const updatedMessages = state.messages.map((msg) => {
        if (!msg.decrypted && msg.recipientUuid === user.uuid && msg.encryptedData) {
          try {
            const decrypted = encryption.decryptMessage(msg, privateKey);
            if (decrypted && decrypted !== "[Decryption Failed]") {
              decryptedCount++;
              return { ...msg, decrypted };
            } else {
              return { ...msg, decrypted: "[Failed to decrypt - wrong key?]" };
            }
          } catch (error) {
            console.error(`Failed to decrypt message ${msg.id}:`, error);
            return { ...msg, decrypted: "[Decryption Error]" };
          }
        }
        return msg;
      });
      
      if (decryptedCount > 0) {
        dispatch({ type: ACTIONS.SET_MESSAGES, payload: updatedMessages });
        showNotification(`Successfully decrypted ${decryptedCount} message(s)!`, "success");
        return true;
      } else {
        const hasUndecrypted = state.messages.some(
          msg => !msg.decrypted && msg.recipientUuid === user.uuid
        );
        if (hasUndecrypted) {
          showNotification("No messages could be decrypted. Make sure you're using the correct private key for this account.", "warning");
        } else {
          showNotification("All messages are already decrypted!", "info");
        }
        return false;
      }
    } catch (error) {
      console.error("Decryption error:", error);
      showNotification("Failed to decrypt messages: " + error.message, "error");
      return false;
    }
  }, [state.messages, user.uuid, showNotification]);

  const hasPrivateKey = useCallback(() => {
    return !!encryption.getPrivateKey();
  }, []);

  const handleTyping = useCallback(() => {
    if (!selectedContact) return;
    
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    socket.emit('typing', {
      recipientUuid: selectedContact.contactUuid,
      isTyping: true
    });
    
    typingTimeoutRef.current = setTimeout(() => {
      socket.emit('typing', {
        recipientUuid: selectedContact.contactUuid,
        isTyping: false
      });
    }, 2000);
  }, [selectedContact]);

  useEffect(() => {
    if (!isAuthenticated || !jwtToken || socketListenersSetup.current) return;

    socketListenersSetup.current = true;

    const handleReceiveMessage = (data) => {
      console.log("📨 Received message:", data);
      const isForMe = data.recipientUuid === user.uuid;

      if (isForMe) {
        let decryptedContent = null;
        const privateKey = encryption.getPrivateKey();
        if (privateKey) {
          try {
            decryptedContent = encryption.decryptMessage(
              {
                encryptedData: data.encryptedData,
                iv: data.iv,
                senderEncryptedKey: data.senderEncryptedKey,
                recipientEncryptedKey: data.recipientEncryptedKey,
              },
              privateKey,
              false
            );
          } catch (error) {
            console.error("Auto-decryption failed:", error);
          }
        }

        const newMessage = {
          id: data.id,
          senderUuid: data.senderUuid,
          recipientUuid: user.uuid,
          senderToken: data.senderToken,
          recipientToken: data.recipientToken,
          senderEncryptedKey: data.senderEncryptedKey,
          recipientEncryptedKey: data.recipientEncryptedKey,
          encryptedData: data.encryptedData,
          iv: data.iv,
          sessionId: data.sessionId,
          timestamp: new Date(data.timestamp),
          expiresAt: data.expiresAt ? new Date(data.expiresAt) : null,
          isSelfDestruct: data.isSelfDestruct,
          decrypted: decryptedContent,
          isOwn: false,
          deliveredAt: null,
          readAt: null,
          hasAttachment: data.hasAttachment || false,
          attachment: data.attachment || null,
        };

        addMessage(newMessage);

        if (
          !selectedContact ||
          data.senderUuid !== selectedContact.contactUuid
        ) {
          incrementUnread(data.senderUuid);
        } else {
          setTimeout(() => markAsRead(data.id), 100);
        }

        markAsDelivered(data.id);

        const contact = contacts.find((c) => c.contactUuid === data.senderUuid);
        const senderName =
          contact?.alias || data.senderUuid.substring(0, 8) + "...";
        
        const notificationMessage = data.hasAttachment 
          ? `📎 New file from ${senderName}`
          : `📩 New message from ${senderName}`;
        
        showNotification(notificationMessage, "info", 3000);

        setTimeout(() => scrollToBottom("smooth"), 100);
      }
    };

    const handleMessageDelivered = (data) => {
      updateMessage(data.messageId, { deliveredAt: new Date(data.timestamp) });
    };

    const handleMessageRead = (data) => {
      updateMessage(data.messageId, { readAt: new Date(data.timestamp) });
    };

    const handleMessageAcknowledged = (data) => {
      updateMessage(data.tempId, {
        id: data.messageId,
        deliveredAt: data.delivered ? new Date(data.timestamp) : null,
      });
    };

    const handleUserTyping = ({ senderUuid, isTyping }) => {
      if (selectedContact && senderUuid === selectedContact.contactUuid) {
        setTyping(senderUuid, isTyping);
      }

      if (isTyping) {
        setTimeout(() => {
          setTyping(senderUuid, false);
        }, 3000);
      }
    };

    const handleUnreadMessages = ({ messages: unreadMessages }) => {
      unreadMessages.forEach((msg) => {
        addMessage({
          ...msg,
          timestamp: new Date(msg.timestamp),
          decrypted: null,
          isOwn: msg.senderUuid === user.uuid,
          senderEncryptedKey: msg.senderEncryptedKey,
          recipientEncryptedKey: msg.recipientEncryptedKey,
          hasAttachment: msg.hasAttachment || false,
          attachment: msg.attachment || null,
        });
        if (
          msg.senderUuid !== user.uuid &&
          (!selectedContact || msg.senderUuid !== selectedContact.contactUuid)
        ) {
          incrementUnread(msg.senderUuid);
        }
      });
      showNotification(
        `You have ${unreadMessages.length} unread messages`,
        "info",
      );
    };

    const handleFileAttachmentReceived = (data) => {
      console.log("📎 File attachment received:", data);
    };

    const handleFileDownloadReady = (data) => {
      console.log("📥 File download ready:", data);
      window.dispatchEvent(new CustomEvent('fileDownloadReady', { detail: data }));
    };

    const handleFileDownloadError = (data) => {
      console.error("📥 File download error:", data);
      window.dispatchEvent(new CustomEvent('fileDownloadError', { detail: data }));
    };

    socket.on("receiveMessage", handleReceiveMessage);
    socket.on("messageDelivered", handleMessageDelivered);
    socket.on("messageRead", handleMessageRead);
    socket.on("messageAcknowledged", handleMessageAcknowledged);
    socket.on("userTyping", handleUserTyping);
    socket.on("unreadMessages", handleUnreadMessages);
    socket.on("fileAttachmentReceived", handleFileAttachmentReceived);
    socket.on("fileDownloadReady", handleFileDownloadReady);
    socket.on("fileDownloadError", handleFileDownloadError);
    socket.on("socket:connected", () => {
      console.log("✅ Socket connected");
    });

    return () => {
      socket.off("receiveMessage", handleReceiveMessage);
      socket.off("messageDelivered", handleMessageDelivered);
      socket.off("messageRead", handleMessageRead);
      socket.off("messageAcknowledged", handleMessageAcknowledged);
      socket.off("userTyping", handleUserTyping);
      socket.off("unreadMessages", handleUnreadMessages);
      socket.off("fileAttachmentReceived", handleFileAttachmentReceived);
      socket.off("fileDownloadReady", handleFileDownloadReady);
      socket.off("fileDownloadError", handleFileDownloadError);
      socketListenersSetup.current = false;
    };
  }, [
    isAuthenticated,
    jwtToken,
    user.uuid,
    selectedContact,
    contacts,
    addMessage,
    updateMessage,
    setTyping,
    incrementUnread,
    markAsDelivered,
    markAsRead,
    showNotification,
    scrollToBottom,
  ]);

  useEffect(() => {
    if (isAuthenticated) {
      loadMessages();
    }
  }, [isAuthenticated, loadMessages]);

  useEffect(() => {
    const hasPrivateKey = !!encryption.getPrivateKey();
    const hasMessages = state.messages.length > 0;
    const notInProgress = !decryptionInProgress.current;

    console.log('[🔍 Decryption] Condition check:', {
      hasPrivateKey,
      messageCount: state.messages.length,
      notInProgress,
      userUuid: user.uuid
    });

    if (hasPrivateKey && hasMessages && notInProgress) {
      console.log('[🔓 Decryption] Starting decryption process...');
      decryptionInProgress.current = true;
      const privateKey = encryption.getPrivateKey();

      const decryptedMessages = state.messages.map((msg, index) => {
        const shouldDecrypt = !msg.decrypted && msg.recipientUuid === user.uuid;
        
        console.log(`[📦 Msg ${index}] ID: ${msg.id || 'N/A'} | shouldDecrypt: ${shouldDecrypt}`, {
          recipientUuidMatch: msg.recipientUuid === user.uuid,
          alreadyDecrypted: !!msg.decrypted
        });

        if (shouldDecrypt) {
          const isOwn = msg.senderUuid === user.uuid;
          const payload = {
            encryptedData: msg.encryptedData,
            iv: msg.iv,
            senderEncryptedKey: msg.senderEncryptedKey,
            recipientEncryptedKey: msg.recipientEncryptedKey,
            encryptedKey: msg.encryptedKey,
          };

          const missingFields = Object.entries(payload)
            .filter(([_, val]) => val === undefined || val === null || val === '')
            .map(([key]) => key);
            
          if (missingFields.length > 0) {
            console.warn(`[⚠️ Msg ${index}] Missing fields for decryption:`, missingFields);
          } else {
            console.log(`[✅ Msg ${index}] All required fields present. isOwn: ${isOwn}`);
          }

          const decrypted = encryption.decryptMessage(payload, privateKey, isOwn);
          console.log(`[🔐 Msg ${index}] Decryption result:`, decrypted ? 'SUCCESS' : 'FAILED');
          
          return { ...msg, decrypted };
        }
        return msg;
      });

      const changed = JSON.stringify(decryptedMessages) !== JSON.stringify(state.messages);
      console.log('[🔄 Decryption] State comparison:', { changed, prevCount: state.messages.length, newCount: decryptedMessages.length });

      if (changed) {
        console.log('[📤 Decryption] Dispatching updated messages');
        dispatch({ type: ACTIONS.SET_MESSAGES, payload: decryptedMessages });
      } else {
        console.log('[⏭️ Decryption] No changes detected, skipping dispatch');
      }

      decryptionInProgress.current = false;
      console.log('[✅ Decryption] Process complete');
    } else {
      console.log('[🛑 Decryption] Skipped. Missing:', {
        privateKey: hasPrivateKey ? '✅' : '❌',
        messages: hasMessages ? '✅' : '❌',
        notInProgress: notInProgress ? '✅' : '❌'
      });
    }
  }, [state.messages, user.uuid]);

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  const value = {
    ...state,
    messagesEndRef,
    messagesContainerRef,
    isSelfDestructModalOpen,
    setIsSelfDestructModalOpen,
    scrollToBottom,
    handleScroll,
    loadMessages,
    sendMessage,
    deleteMessage,
    markAsDelivered,
    markAsRead,
    setTyping,
    clearMessages,
    addMessage,
    updateMessage,
    incrementUnread,
    resetUnread,
    setCurrentMessage,
    setPendingSelfDestructMessage,
    setPendingFile,
    clearPendingFile,
    decryptAllMessages,
    hasPrivateKey,
    handleTyping,
    dispatch,
    ACTIONS,
  };

  return (
    <MessagesContext.Provider value={value}>
      {children}
    </MessagesContext.Provider>
  );
}

export function useMessages() {
  const context = React.useContext(MessagesContext);
  if (!context) {
    throw new Error("useMessages must be used within MessagesProvider");
  }
  return context;
}